//callbacks/recipeCallbacks.ts
import {
    buildEntityCallbacks,
    EntityOnPreSaveProps,
    EntityOnDeleteProps,
    EntityOnSaveProps,
  } from "@firecms/core";
  import { recipesCollection } from "../collections/recipes";

  import { Recipe } from "../types/recipeTypes";

  /**
   * Callbacks for the recipes collection.
   */
  export const recipeCallbacks = buildEntityCallbacks<Recipe>({
    /**
     * Before saving a recipe, update `modified_at` and `modified_by` fields.
     */
    onPreSave: async ({
      values,
      context,
    }: EntityOnPreSaveProps<Recipe, any>): Promise<Partial<Recipe>> => {
      return {
        ...values,
        modified_at: new Date(),
        modified_by: context.authController.user?.email ?? "Unknown User",
      };
    },
  
    /**
     * After saving a recipe, regenerate the `recipes.json` file in Firebase Storage.
     */
    onSaveSuccess: async ({ context }: EntityOnSaveProps<Recipe>): Promise<void> => {
      console.log("onSaveSuccess triggered"); // Debug log
  
      try {
          // Fetch recipes collection
          const recipes = await context.dataSource.fetchCollection({
              path: "recipes",
              collection: recipesCollection,
          });
  
          console.log("Fetched recipes:", recipes);
  
          // Ensure recipes are sorted by "order" field before saving
          const sortedRecipes = recipes.sort((a, b) => (a.values.order ?? 0) - (b.values.order ?? 0));
  
          console.log("Sorted recipes:", sortedRecipes);
  
          // Convert sorted recipes to JSON
          const jsonContent = JSON.stringify(sortedRecipes, null, 2); // Pretty-print for readability
  
          // Upload sorted recipes.json to Firebase Storage
          await context.storageSource.uploadFile({
              file: new File([jsonContent], "recipes.json", {
                  type: "application/json",
              }),
              path: "recipes", // Ensure no trailing slash here
              fileName: "recipes.json",
              metadata: {
                  cacheControl: "public, max-age=3600", // Helps with caching
                  contentType: "application/json",
                  firebaseStorageDownloadTokens: "", // Ensures it's publicly accessible
              },
          });
  
          console.log("Successfully uploaded sorted recipes.json to Firebase Storage");
      } catch (error) {
          console.error(
              `Failed to save recipes.json: ${
                  error instanceof Error ? error.message : "Unknown error"
              }`
          );
      }
  },
  
    
    /**
     * Before deleting a recipe, ensure the user is authorized to delete it.
     */
    onPreDelete: async ({
      entity,
      context,
    }: EntityOnDeleteProps<Recipe>): Promise<void> => {
      if (context.authController.user?.email !== entity.values.modified_by) {
        throw new Error("You are not authorized to delete this recipe.");
      }
    },
  
    /**
     * After deleting a recipe, log the deletion.
     */
    onDelete: async ({ entity }: EntityOnDeleteProps<Recipe>): Promise<void> => {
      console.log(`Recipe deleted: ${entity.values.name}`);
    },
  });
  